import { Heading, Stack, Tab, TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/react';
import { useTranslations } from 'use-intl';

import { getBrand, getHostName, isFeatureEnabled } from '@blockpulse3/data/shared';
import { UserRole } from '@blockpulse3/graphql/hooks';
import { LinkButton } from '@blockpulse3/ui/commons';
import { useUser } from '@blockpulse3/web-client/auth';

import { AssetsAndPoolsView } from './AssetsAndPools';
import { CapTableView } from './CapTable';
import { ShareTransferRegisterView } from './ShareTransferRegister';

type Props = {
  backHref?: string;
};

/**
 * ShareholdingView.
 * Main shareholding view, rendering CaptableView and ShareTransferRegisterView (RMT).
 *
 * @returns {JSX.Element}
 */
export function ShareholdingView({ backHref }: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nShareholding = useTranslations('Shareholding');

  const { user } = useUser();

  const isSuperAdmin = user?.roles.includes(UserRole.SUPERADMIN);
  const isFeatureOptionPool = isFeatureEnabled(getBrand(getHostName()), 'optionPool');

  return (
    <Stack spacing="6">
      {backHref && <LinkButton label={i18nGeneral('Back')} route={backHref} />}
      <Heading>{i18nShareholding('Title')}</Heading>
      <Tabs isLazy>
        <TabList>
          <Tab>{i18nShareholding('CapTable')}</Tab>
          <Tab>{i18nShareholding('ShareTransferRegister')}</Tab>
          {isSuperAdmin && isFeatureOptionPool && <Tab>{i18nShareholding('AssetsAndPlans')}</Tab>}
        </TabList>
        <TabPanels>
          <TabPanel px="0">
            <CapTableView />
          </TabPanel>
          <TabPanel px="0">
            <ShareTransferRegisterView />
          </TabPanel>
          {isSuperAdmin && isFeatureOptionPool && (
            <TabPanel px="0">
              <AssetsAndPoolsView />
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>
    </Stack>
  );
}

export type ShareholdingViewProps = Props;
